import { useEffect, useState } from "react";
import { Sparkles, Loader2, Wand2, Search } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type FieldKey = "email" | "phone" | "mobilePhone" | "jobTitle" | "linkedinUrl";
type SourceMap = Partial<Record<FieldKey, "apollo" | "hunter">>;
const SOURCE_LABEL: Record<"apollo" | "hunter", string> = {
  apollo: "Apollo",
  hunter: "Hunter",
};

type ExtractedContact = {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  jobTitle: string | null;
  companyName: string | null;
  linkedinUrl: string | null;
};

type Draft = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  linkedinUrl: string;
};

const EMPTY_DRAFT: Draft = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  jobTitle: "",
  linkedinUrl: "",
};

function fillDraft(extracted: ExtractedContact): Draft {
  return {
    firstName: extracted.firstName ?? "",
    lastName: extracted.lastName ?? "",
    email: extracted.email ?? "",
    phone: extracted.phone ?? "",
    jobTitle: extracted.jobTitle ?? "",
    linkedinUrl: extracted.linkedinUrl ?? "",
  };
}

export function AddContactWithAIDialog({
  open,
  onOpenChange,
  clientId,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  clientId?: string | null;
  onCreated?: (contactId: string) => void;
}) {
  const utils = trpc.useUtils();
  const [rawText, setRawText] = useState("");
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [extracted, setExtracted] = useState<ExtractedContact | null>(null);
  const [sources, setSources] = useState<SourceMap>({});

  useEffect(() => {
    if (!open) {
      setRawText("");
      setDraft(EMPTY_DRAFT);
      setExtracted(null);
      setSources({});
    }
  }, [open]);

  const extract = trpc.contacts.extractFromText.useMutation({
    onSuccess: (data) => {
      setExtracted(data);
      setDraft(fillDraft(data));
      setSources({});
      const filledCount = [
        data.firstName,
        data.lastName,
        data.email,
        data.phone,
        data.jobTitle,
      ].filter(Boolean).length;
      if (filledCount === 0) {
        toast.warning("AI didn't find any contact details — try a different snippet.");
      } else {
        toast.success(`Extracted ${filledCount} field${filledCount > 1 ? "s" : ""}. Review and save.`);
      }
    },
    onError: (err) => toast.error(err.message),
  });

  const findDetails = trpc.contacts.findContactDetails.useMutation({
    onSuccess: (data) => {
      const f = data.found;
      const next: Draft = { ...draft };
      const nextSources: SourceMap = { ...sources };
      const filledNow: string[] = [];
      const apply = (key: keyof Draft, value: string | null, src?: "apollo" | "hunter") => {
        if (next[key].trim().length > 0) return;
        if (!value) return;
        next[key] = value;
        filledNow.push(key);
        if (src) nextSources[key as FieldKey] = src;
      };
      apply("email", f.email, data.sources.email);
      apply("phone", f.phone ?? f.mobilePhone, data.sources.phone ?? data.sources.mobilePhone);
      apply("jobTitle", f.jobTitle, data.sources.jobTitle);
      apply("linkedinUrl", f.linkedinUrl, data.sources.linkedinUrl);
      setDraft(next);
      setSources(nextSources);
      if (filledNow.length === 0) {
        const apolloStatus = data.providers.find((p) => p.key === "apollo")?.status;
        const hunterStatus = data.providers.find((p) => p.key === "hunter")?.status;
        if (apolloStatus === "skipped" && hunterStatus === "skipped") {
          toast.warning("No API keys for Apollo or Hunter — add one in settings.");
        } else {
          toast.warning("Apollo and Hunter didn't return any new details.");
        }
      } else {
        toast.success(`Found ${filledNow.length} field${filledNow.length > 1 ? "s" : ""}.`);
      }
    },
    onError: (err) => toast.error(err.message),
  });

  const create = trpc.contacts.create.useMutation({
    onSuccess: (row) => {
      toast.success("Contact created");
      utils.contacts.list.invalidate();
      if (clientId) utils.contacts.listByClient.invalidate({ clientId });
      onOpenChange(false);
      onCreated?.(row.id);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleExtract = () => {
    const text = rawText.trim();
    if (text.length < 2) {
      toast.error("Paste some text first.");
      return;
    }
    extract.mutate({ text });
  };

  const handleFind = () => {
    if (!clientId) return;
    const firstName = draft.firstName.trim();
    if (!firstName) {
      toast.error("Need at least a first name to look up.");
      return;
    }
    findDetails.mutate({
      clientId,
      firstName,
      lastName: draft.lastName.trim() || null,
      email: draft.email.trim() || null,
      linkedinUrl: draft.linkedinUrl.trim() || null,
    });
  };

  const handleSave = () => {
    const firstName = draft.firstName.trim() || null;
    const lastName = draft.lastName.trim() || null;
    const email = draft.email.trim() || null;
    const phone = draft.phone.trim() || null;
    const jobTitle = draft.jobTitle.trim() || null;
    const linkedinUrl = draft.linkedinUrl.trim() || null;

    if (!firstName && !lastName && !email) {
      toast.error("Need at least a name or email.");
      return;
    }

    create.mutate({
      clientId: clientId ?? null,
      firstName,
      lastName,
      email,
      phone,
      jobTitle,
      linkedinUrl,
    });
  };

  const hasExtracted = extracted !== null;
  const busy = extract.isPending || create.isPending || findDetails.isPending;
  const canFind =
    hasExtracted &&
    Boolean(clientId) &&
    draft.firstName.trim().length > 0 &&
    (draft.email.trim().length === 0 || draft.phone.trim().length === 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Add contact with AI
          </DialogTitle>
          <DialogDescription>
            Paste an email signature, an email body, or any text containing
            contact details. AI will extract name, email, phone, and role.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
              Pasted text
            </Label>
            <Textarea
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              rows={6}
              placeholder={
                "e.g.\n\nBest regards,\nJonas Petraitis\nCFO, ACME UAB\n+370 600 12345\njonas@acme.lt"
              }
              autoFocus
            />
            <div className="mt-2 flex justify-end">
              <Button
                type="button"
                onClick={handleExtract}
                disabled={busy || rawText.trim().length < 2}
                variant={hasExtracted ? "outline" : "default"}
                size="sm"
              >
                {extract.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Wand2 className="mr-1.5 h-3.5 w-3.5" />
                )}
                {hasExtracted ? "Re-extract" : "Extract with AI"}
              </Button>
            </div>
          </div>

          {hasExtracted && (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  Review the extracted fields before saving.
                  {extracted?.companyName ? (
                    <>
                      {" "}
                      Detected company:{" "}
                      <span className="font-medium text-foreground">
                        {extracted.companyName}
                      </span>
                      .
                    </>
                  ) : null}
                </p>
                {clientId ? (
                  <Button
                    type="button"
                    onClick={handleFind}
                    disabled={busy || !canFind}
                    variant="outline"
                    size="sm"
                    title={
                      !draft.firstName.trim()
                        ? "Add a first name first."
                        : draft.email.trim() && draft.phone.trim()
                          ? "Email and phone already filled."
                          : "Look up email and phone via Apollo and Hunter."
                    }
                  >
                    {findDetails.isPending ? (
                      <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Search className="mr-1.5 h-3.5 w-3.5" />
                    )}
                    Find email &amp; phone
                  </Button>
                ) : null}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field
                  label="First name"
                  value={draft.firstName}
                  onChange={(v) => setDraft({ ...draft, firstName: v })}
                />
                <Field
                  label="Last name"
                  value={draft.lastName}
                  onChange={(v) => setDraft({ ...draft, lastName: v })}
                />
                <Field
                  label="Email"
                  type="email"
                  value={draft.email}
                  onChange={(v) => setDraft({ ...draft, email: v })}
                  source={sources.email}
                />
                <Field
                  label="Phone"
                  type="tel"
                  value={draft.phone}
                  onChange={(v) => setDraft({ ...draft, phone: v })}
                  source={sources.phone ?? sources.mobilePhone}
                />
                <Field
                  label="Role / title"
                  value={draft.jobTitle}
                  onChange={(v) => setDraft({ ...draft, jobTitle: v })}
                  source={sources.jobTitle}
                />
                <Field
                  label="LinkedIn"
                  type="url"
                  value={draft.linkedinUrl}
                  onChange={(v) => setDraft({ ...draft, linkedinUrl: v })}
                  source={sources.linkedinUrl}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              busy ||
              !hasExtracted ||
              (!draft.firstName.trim() &&
                !draft.lastName.trim() &&
                !draft.email.trim())
            }
          >
            {create.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : null}
            Save contact
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  source,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "email" | "tel" | "url";
  source?: "apollo" | "hunter";
}) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <Label className="block text-[10px] uppercase tracking-wide text-muted-foreground">
          {label}
        </Label>
        {source ? (
          <span className="rounded-sm bg-primary/10 px-1.5 py-px text-[10px] font-medium text-primary">
            via {SOURCE_LABEL[source]}
          </span>
        ) : null}
      </div>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8"
      />
    </div>
  );
}
